/**
 * Out-of-Pocket Requests View
 *
 * Displays out-of-pocket expense requests for assistants who need to process
 * equipment purchases and reimbursements.
 */

import { renderIcon } from '../utils/icons';
import { createRequestList, type ExpenditureRequest } from '../components/request-list';
import { createSidebarLayout } from '../components/sidebar-layout';
import { exportRequestsToCSV } from '../utils/csv-export';
import { showToast } from '../components/toast';

/**
 * Create the Out-of-Pocket Requests view
 */
export function createOutOfPocketView(): HTMLElement {
  const content = document.createElement('div');
  content.className = 'px-4 lg:px-6 py-6';

  // Out-of-Pocket badge and header
  const header = document.createElement('div');
  header.className = 'mb-6';
  header.innerHTML = `
    <div class="flex items-center gap-2 mb-2">
      <span class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-purple-500/20 text-purple-400">
        ${renderIcon('wallet', { size: 'xs' }).outerHTML}
        Out-of-Pocket
      </span>
    </div>
    <p class="text-sm text-muted-foreground mt-1">Out-of-pocket expenses for reimbursement processing</p>
  `;
  content.appendChild(header);

  // Request list container
  const requestListContainer = document.createElement('div');
  requestListContainer.id = 'request-list-container';
  content.appendChild(requestListContainer);

  // Initialize request list after DOM is ready
  setTimeout(() => {
    let allRequests: ExpenditureRequest[] = [];

    const requestList = createRequestList({
      parent: requestListContainer,
      title: 'Out-of-Pocket Requests',
      subtitle: 'Out-of-pocket expense requests',
      endpoint: '/requests',
      showFilters: true,
      showSearch: true,
      showStatusColumn: true,
      showCreatorColumn: true,
      emptyMessage: 'No out-of-pocket requests found.',
      initialFilters: {
        category: 'EQUIPMENT',
      },
      enableBulkSelection: true,
      showBulkActions: true,
      onBulkExport: (ids: string[]) => {
        const selectedRequests = allRequests.filter(r => ids.includes(r.id));
        exportRequestsToCSV(selectedRequests);
        showToast({
          type: 'success',
          message: `Exported ${selectedRequests.length} request${selectedRequests.length > 1 ? 's' : ''} to CSV`,
        });
      },
      viewId: 'out-of-pocket',
      showColumnPicker: true,
      hideCategoryFilter: true,
      hiddenColumns: ['category'],
    });

    // Store all requests for export functionality
    const originalMethod = Object.getPrototypeOf(requestList).loadRequests;
    if (originalMethod) {
      Object.getPrototypeOf(requestList).loadRequests = async function(...args: any[]) {
        const result = await originalMethod.apply(this, args);
        allRequests = this.allRequests.length > 0 ? this.allRequests : this.requests;
        return result;
      };
    }
  }, 0);

  return createSidebarLayout(content, {
    currentPath: '/out-of-pocket',
    title: 'Out-of-Pocket',
    subtitle: 'Process equipment purchases and reimbursements',
  });
}

/**
 * Out-of-Pocket view function for router
 */
export function outOfPocketView(): HTMLElement {
  return createOutOfPocketView();
}
